import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps, match } from 'react-router-dom';
import Button from '@material-ui/core/Button';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas, FormPickerDatas, ListCell } from '../../../local-types';
import { LCAlert } from '../../../components/alert';
import { Dialog } from '../../../components/dialog';
import { AutoFill } from '../../../components/field-autofill';
import EnhancedListItem from '../../../components/listItem';
import MainLoading from '../../../components/main-loading';
import BookingBillView from '../bill';
// Modules
import {
    actions as bookingActions,
    selectors as bookingSelectors,
    types as bookingTypes,
    BookingById,
} from '../../../redux/reducers/bookings';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { selectors as errorsSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import { BookingModule, RecalculRefoundData, RecalculRefundFormData, TParams } from '../../../redux/reducers/api-types';
// Ressources
import FormattingUtils from '../../../utils/formattingUtils';
import { AccountRight } from '../../../utils/userUtils';
import styles from './booking-refund-style';
import { paths } from '../../paths';
import StringUtils from '../../../utils/stringUtils';
import { getGeoAutocompleteURL } from '../../../utils/core-api';

interface BookingRefundState {
    formData: any;
    formRecalculate: RecalculRefundFormData;
    inputs: FormDatas[];
    recalculateResponse: RecalculRefoundData;
}

interface BookingRefund {
    label: string;
    ride_id: string;
    customer_bill_id: string;
    product_type?: 'adjustment_transport' | 'adjustment_service';
    partner_id?: string;
    with_tax_customer?: number;
    pre_tax_partner?: number;
}

interface BookingRefundFormData {
    label: string;
    motif: 'default' | 'adjustment_transport' | 'adjustment_service';
    driver?: string;
    booking?: any;
    bill_number?: string;
    with_tax_customer?: number;
    pre_tax_partner?: number;
}

interface BookingRefundProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    bookings: BookingById;
    isRecalculatingRefund: boolean;
    isSavingRefund: boolean;
    getBooking: (id: string) => void;
    saveRefound: (id: string, payload: BookingRefund) => void;
    recalculateRefound: (id: string, payload: RecalculRefundFormData, callback: Function) => void;
    bookingsErrors: ErrorPayload;
    refoundErrors: ErrorPayload;
    recalculateRefoundError: ErrorPayload;
}

class BookingRefoundPage extends React.Component<BookingRefundProps, BookingRefundState> {
    optionsType: FormPickerDatas[];
    optionsDriver: FormPickerDatas[];
    myRef: any;
    myDialogRef: any;
    myFormRef: any;

    state = {
        formData: {} as any,
        formRecalculate: {} as RecalculRefundFormData,
        inputs: [],
        recalculateResponse: {
            // pre_tax_customer: 36.35,
            // pre_tax_partner: 29.08,
            // with_tax_customer: 39.99,
            // with_tax_partner: 31.99,
        } as RecalculRefoundData,
    };

    componentDidMount() {
        const { match } = this.props;
        const id = match.params.id;
        if (id) {
            this.props.getBooking(id);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { match, bookings } = this.props;
        const id: string = match.params.id;
        const prevId: string = prevProps.match.params.id;
        const payload = bookings[id];

        if (prevId !== id || prevProps.bookings[prevId] !== bookings[id]) {
            this.inputsFactory(payload);
        }
    }

    inputsFactory = (payload) => {
        const { t } = this.props;
        let title: string = '';
        let isSimpleBilling: boolean = false;
        let isMensualBilling: boolean = false;
        if (payload.bills.length > 0) {
            isSimpleBilling = true;
            title = t('refound.immediat');
        }
        if (payload.grouped_bills.length > 0) {
            isMensualBilling = true;
            title = t('refound.mensual');
        }

        if (isSimpleBilling || isMensualBilling) {
            let driver = null;
            if (payload.driver) {
                driver = Object.assign({}, payload.driver);
                driver.name = StringUtils.userFullname(driver);
            }

            const drivers = [driver, ...payload.rejections.map((r) => r.driver)];

            this.optionsDriver = drivers
                .filter((d) => d)
                .map((d) => {
                    d.name = StringUtils.userFullname(d);
                    return d;
                });

            const inputs: FormDatas[] = [
                {
                    title: `${t('refound.title')} - ${title}`,
                    datas: [
                        !isMensualBilling && {
                            id: 'bill_number',
                            label: t('refound.bill_refound'),
                            disable: true,
                            defaultValue: `${t('refound.bill')} N°${
                                payload.bill_billing?.bill_number
                            } (${FormattingUtils.price(payload.bill_billing?.amount_with_tax)})`,
                        },
                        {
                            id: 'label',
                            label: t('refound.reason'),
                            inputList: this.getOptionsReason(),
                            required: true,
                        },
                        !isMensualBilling && {
                            id: 'motif',
                            label: t('refound.type'),
                            options: this.getOptionsRefund(),
                            hint: t('bill:credit.hint'),
                            noEmpty: true,
                            required: true,
                        },
                        {
                            id: 'with_tax_customer',
                            label: t(
                                isMensualBilling
                                    ? 'refound.credit_amount_price_mensual'
                                    : 'refound.credit_amount_price',
                                { price: payload.order?.base_price_with_vat },
                            ),
                            placeholder: t('refound.max_TTC', {
                                price: isMensualBilling
                                    ? payload.order?.base_price_with_vat
                                    : -payload.order?.base_price_with_vat,
                            }),
                            hint: t(isMensualBilling ? 'refound.customer_hint_mensual' : 'refound.customer_hint'),
                            type: 'number',
                            numberNegative: true,
                            onlyNegative: !isMensualBilling,
                        },
                        // {
                        //     id: 'adjustment_driver_auto',
                        //     label: t('refound.adjustment_driver_auto'),
                        //     type: 'boolean',
                        // },
                        {
                            id: 'pre_tax_partner',
                            label: t('refound.adjustment'),
                            type: 'number',
                            numberNegative: true,
                        },
                        // {
                        //     id: 'adjustment_driver_percent',
                        //     label: t('refound.adjustment_driver_percent'),
                        //     type: 'number',
                        //     inputsAttributs: { step: 0.01, pattern: '[0-9]' },
                        //     numberNegative: true,
                        // },
                        {
                            id: 'driver',
                            label: t('refound.driver'),
                            options: this.optionsDriver,
                        },
                    ],
                },
                {
                    customView: payload && <BookingBillView booking={payload} />,
                },
            ];
            this.setState({ inputs });
        }
    };

    generateInput = (): FormDatas[] => {
        const { t, isRecalculatingRefund, recalculateRefoundError } = this.props;
        const { recalculateResponse } = this.state;

        const booking: any = {} as any;
        booking.products_billing = recalculateResponse?.products;
        booking.ride_billing = {
            price_explain: recalculateResponse?.price_explain,
            pre_tax_customer: recalculateResponse?.pre_tax_customer,
            with_tax_customer: recalculateResponse?.with_tax_customer,
            pre_tax_partner: recalculateResponse?.pre_tax_partner,
            with_tax_partner: recalculateResponse?.with_tax_partner,
        };

        const inputs: FormDatas[] = [
            {
                title: `${t('refound.recalculate')}`,
                datas: [
                    {
                        id: 'pu',
                        label: t('reservation:pu'),
                        customView: (val, handleChange) => {
                            return (
                                <AutoFill
                                    datas={{
                                        id: 'pu',
                                        label: t('reservation:recurrents.infos.address_pu'),
                                        style: {
                                            minWidth: 200,
                                            width: '100%',
                                            flex: 1,
                                            marginRight: 8,
                                            paddingRight: 32,
                                        },
                                        variant: 'outlined',
                                        location: true,
                                        locationNoDetails: true,
                                        minChar: 3,
                                        required: false,
                                        onChange: handleChange,
                                        api: getGeoAutocompleteURL(),
                                    }}
                                />
                            );
                        },
                    },
                    {
                        id: 'do',
                        label: t('reservation:do'),
                        hint: t('refound.do_hint'),
                        customView: (val, handleChange, formData, hint) => {
                            return (
                                <>
                                    <AutoFill
                                        datas={{
                                            id: 'do',
                                            label: t('reservation:recurrents.infos.address_do'),
                                            style: {
                                                minWidth: 200,
                                                width: '100%',
                                                flex: 1,
                                                marginRight: 8,
                                                paddingRight: 32,
                                            },
                                            variant: 'outlined',
                                            location: true,
                                            locationNoDetails: true,
                                            minChar: 3,
                                            required: false,
                                            onChange: handleChange,
                                            api: getGeoAutocompleteURL(),
                                        }}
                                    />
                                    <div
                                        style={{
                                            marginLeft: 16,
                                            marginRight: 16,
                                        }}
                                    >
                                        {hint}
                                    </div>
                                </>
                            );
                        },
                    },
                    {
                        id: 'ride_infos.ride_distance',
                        label: t('refound.ride_distance'),
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'ride_infos.ride_time',
                        label: t('refound.ride_time'),
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'ride_infos.waiting_time',
                        label: t('refound.waiting_time'),
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'ride_infos.driver_surge',
                        label: t('refound.driver_surge'),
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'ride_infos.surge',
                        label: t('refound.surge'),
                        type: 'number',
                        numberNegative: false,
                    },
                ],
            },
            {
                customView: isRecalculatingRefund ? (
                    <MainLoading center />
                ) : recalculateRefoundError ? (
                    <ErrorMessage payload={recalculateRefoundError} />
                ) : (
                    Object.keys(recalculateResponse).length > 0 && (
                        <>
                            <BookingBillView
                                title={t('refound.recalculation_preview')}
                                booking={booking as BookingModule}
                            />
                            <EnhancedListItem
                                datas={this.generateListItem(recalculateResponse)}
                                title={t('refound.recalculation')}
                                cardActions={
                                    <>
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            onClick={this.handleApplyPartnerChanges}
                                        >
                                            {t('refound.partner_use')}
                                        </Button>
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            onClick={this.handleApplyCustomerChanges}
                                        >
                                            {t('refound.customer_use')}
                                        </Button>
                                    </>
                                }
                            />
                        </>
                    )
                ),
            },
        ];
        return inputs;
    };

    generateListItem = (payload: RecalculRefoundData): ListCell[] => {
        const { t, match, bookings } = this.props;
        if (!payload) {
            return [];
        }
        const id = match.params.id;
        const booking = bookings[id];

        const listItem: ListCell[] = [
            {
                title: t('driver'),
                data: t('common:euro_ht_ttc', { ht: payload.pre_tax_partner, ttc: payload.with_tax_partner }),
            },
            {
                title: t('customer'),
                data: t('common:euro_ht_ttc', { ht: payload.pre_tax_customer, ttc: payload.with_tax_customer }),
            },
            {
                title: t('refound.partner'),
                data: t('common:euro_ht_fill', {
                    ht: (payload.pre_tax_partner - booking.ride_billing.pre_tax_partner).toFixed(2),
                }),
            },
            {
                title: t('refound.customer'),
                data: t('common:euro_ttc_fill', {
                    ttc: (payload.with_tax_customer - booking.ride_billing.with_tax_customer).toFixed(2),
                }),
            },
        ];
        return listItem;
    };

    handleApplyPartnerChanges = (e: any) => {
        e.preventDefault();

        const { match, bookings } = this.props;
        const { formData, recalculateResponse } = this.state;
        const id = match.params.id;
        const booking = bookings[id];

        const res = recalculateResponse.pre_tax_partner - booking.ride_billing.pre_tax_partner;
        formData.pre_tax_partner = res.toFixed(2);

        this.setState({
            formData,
        });
        this.myFormRef.setValue('pre_tax_partner', res.toFixed(2));
    };

    handleApplyCustomerChanges = (e: any) => {
        e.preventDefault();

        const { match, bookings } = this.props;
        const { formData, recalculateResponse } = this.state;
        const id = match.params.id;
        const booking = bookings[id];

        const res = recalculateResponse.with_tax_customer - booking.ride_billing.with_tax_customer;
        formData.with_tax_customer = res.toFixed(2);

        this.setState({
            formData,
        });
        this.myFormRef.setValue('with_tax_customer', res.toFixed(2));
    };

    getOptionsReason = (): FormPickerDatas[] => {
        const { t } = this.props;
        const optionsReason: FormPickerDatas[] = [
            { id: t('refound.optionsReason.a'), name: t('refound.optionsReason.a') },
            { id: t('refound.optionsReason.b'), name: t('refound.optionsReason.b') },
            { id: t('refound.optionsReason.c'), name: t('refound.optionsReason.c') },
            { id: t('refound.optionsReason.d'), name: t('refound.optionsReason.d') },
            { id: t('refound.optionsReason.e'), name: t('refound.optionsReason.e') },
        ];
        return optionsReason;
    };

    getOptionsRefund = (): FormPickerDatas[] => {
        const { t } = this.props;
        const optionsRefund: FormPickerDatas[] = [
            {
                id: 'default',
                name: t('bill:credit.type.default'),
            },
            {
                id: 'adjustment_transport',
                name: t('bill:credit.type.adjustment_transport'),
            },
            {
                id: 'adjustment_service',
                name: t('bill:credit.type.adjustment_service'),
            },
        ];
        return optionsRefund;
    };

    handleSubmit = (id: string, formData: BookingRefundFormData) => {
        this.props.saveRefound(id, this.generatePayload(formData));
    };

    generatePayload = (formData: BookingRefundFormData): BookingRefund => {
        const driver: any = this.optionsDriver.find((d) => {
            return String(d.id) === String(formData.driver);
        });

        const payload: BookingRefund = {
            label: formData.label,
            ride_id: formData.booking.uuid,
            customer_bill_id: formData.booking.ride_billing.customer_bill_id,
            ...(formData.motif !== 'default' && { product_type: formData.motif }),
            ...(driver?.partner?.uuid && { partner_id: driver.partner.uuid }),
            ...(formData.with_tax_customer && {
                with_tax_customer: formData.with_tax_customer,
            }),
            ...(formData.pre_tax_partner && {
                pre_tax_partner: formData.pre_tax_partner,
            }),
        };

        return payload;
    };

    submitRefund = (): void => {
        const { match } = this.props;
        const { formData } = this.state;
        const id = match.params.id;

        this.props.saveRefound(id, this.generatePayload(formData as BookingRefundFormData));
    };

    onChange = (formData: any) => {
        this.setState({
            formData,
        });
    };

    onFormSubmit = (formData: BookingRefundFormData) => {
        const { match, bookings } = this.props;
        const id = match.params.id;
        const payload = bookings[id];

        formData.booking = payload;
        this.setState({
            formData: formData,
        });

        const { driver, with_tax_customer, pre_tax_partner } = formData;
        if ((with_tax_customer == null && pre_tax_partner == null) || (pre_tax_partner != null && !driver)) {
            this.myRef.showAlert();
        } else {
            this.myRef.closeAlert();
            this.myDialogRef.showDialog();
        }
    };

    onFormRecalculateSubmit = (formData: any) => {
        const { match } = this.props;
        const id = match.params.id;

        const payload = {
            ...(formData?.pu?.location && {
                start_location: { lat: formData.pu.location[0], lng: formData.pu.location[1] },
            }),
            ...(formData?.do?.location && {
                end_location: { lat: formData.do.location[0], lng: formData.do.location[1] },
            }),
            ...(formData?.ride_infos &&
                Object.values(formData.ride_infos).filter((v) => v).length > 0 && {
                    ride_infos: formData.ride_infos,
                }),
        };

        if (Object.keys(payload).length > 0) {
            this.props.recalculateRefound(id, payload, (response: RecalculRefoundData) => {
                this.setState({ recalculateResponse: response });
            });
        }
    };

    render() {
        const { formData: formPayload, formRecalculate } = this.state;
        const { t, match, bookings, bookingsErrors, refoundErrors, isRecalculatingRefund, isSavingRefund } = this.props;
        const { inputs } = this.state;
        const id = match.params.id;
        const payload = bookings[id];

        return (
            <App needRights={AccountRight.RIDE_ACTION_ADJUSTMENT}>
                <ErrorMessage payload={bookingsErrors} />
                <ErrorMessage payload={refoundErrors} />
                <LCAlert onRef={(ref) => (this.myRef = ref)} details={t('refound.hint')} type="error" open={false} />
                {formPayload && !inputs && <div>{t('common:ushallnotpass')}</div>}
                {formPayload && inputs?.length === 0 && <MainLoading center />}
                {formPayload && inputs?.length > 0 && (
                    <>
                        <Dialog
                            onRef={(ref) => (this.myDialogRef = ref)}
                            btnTitle={t('refound.pop_up_title')}
                            description={t('refound.pop_up_msg', {
                                rider: formPayload.with_tax_customer || 0,
                                driver: formPayload.pre_tax_partner || 0,
                            })}
                            customButton={<></>}
                            onOK={this.submitRefund}
                        />
                        <EnhancedForm
                            onRef={(ref) => (this.myFormRef = ref)}
                            payload={formPayload}
                            inputs={inputs}
                            backRoute={`${paths.bookings}/${id}`}
                            onSubmit={this.onFormSubmit}
                            onChange={this.onChange}
                            loader={isSavingRefund}
                        />
                        {payload && (
                            <EnhancedForm
                                payload={formRecalculate}
                                inputs={this.generateInput()}
                                submitText={t('refound.recalculate_submit')}
                                onSubmit={this.onFormRecalculateSubmit}
                                loader={isRecalculatingRefund}
                            />
                        )}
                    </>
                )}
            </App>
        );
    }
}

const BookingRefoundPageWithStyleAndTranslation = withStyles(styles)(
    withTranslation(['booking', 'bill', 'reservation', 'common'])(BookingRefoundPage),
);

const mapStateToProps = (state) => ({
    bookings: bookingSelectors.bookings(state),
    bookingsErrors: errorsSelectors.getBookingError(state),
    refoundErrors: errorsSelectors.saveBookingRefundError(state),
    recalculateRefoundError: errorsSelectors.getRecalculateRefoundError(state),
    isRecalculatingRefund: pendingSelectors.isRecalculatingRefund(state),
    isSavingRefund: pendingSelectors.isPending(state, bookingTypes.SAVE_REFOUND),
});

const mapDispatchToProps = {
    getBooking: bookingActions.getBooking,
    saveRefound: bookingActions.saveRefound,
    recalculateRefound: bookingActions.recalculateRefound,
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingRefoundPageWithStyleAndTranslation);
